import { useEffect, useState } from "react";
import { MessageCircle, X, Phone } from "lucide-react";
import { CLINIC, buildWaUrl } from "@/lib/data";

export default function FloatingWhatsApp() {
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 800);
    return () => clearTimeout(t);
  }, []);

  const dentalWa = buildWaUrl(
    CLINIC.dental.waNumber,
    "Bună ziua! Doresc o programare la Marimar Dental Clinic."
  );
  const fizioWa = buildWaUrl(
    CLINIC.fizio.waNumber,
    "Bună ziua! Doresc o programare la Marimar Fizio & Kinetoterapie."
  );

  return (
    <div
      data-testid="floating-whatsapp"
      className={`fixed z-[60] bottom-5 right-5 sm:bottom-6 sm:right-6 transition-all duration-700 ${
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      {/* Panel */}
      <div
        className={`absolute bottom-[72px] right-0 w-[300px] sm:w-[340px] origin-bottom-right transition-all duration-300 ${
          open ? "opacity-100 scale-100 translate-y-0 pointer-events-auto" : "opacity-0 scale-95 translate-y-2 pointer-events-none"
        }`}
        data-testid="floating-whatsapp-panel"
      >
        <div className="glass-strong rounded-3xl p-5 shadow-2xl border border-white/80">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-display font-medium text-[16px] text-slate-900 leading-tight">
                Programează-te pe WhatsApp
              </p>
              <p className="text-[12px] text-slate-500 mt-0.5">Răspuns rapid în câteva minute</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
              aria-label="Închide"
              data-testid="floating-whatsapp-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2.5">
            <a
              href={dentalWa}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="floating-whatsapp-dental"
              className="flex items-center gap-3 p-3.5 rounded-2xl bg-white hover:bg-brand-soft border border-slate-100 hover:border-brand-blue transition-all group"
            >
              <span className="w-11 h-11 rounded-full bg-brand-blue text-white flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5" />
              </span>
              <div className="flex-grow min-w-0">
                <p className="font-display text-[14px] font-medium text-slate-900 leading-tight">
                  Marimar Dental Clinic
                </p>
                <p className="text-[12px] text-slate-500 mt-0.5 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {CLINIC.dental.phone}
                </p>
              </div>
              <span className="text-brand-blue opacity-0 group-hover:opacity-100 transition-opacity text-[18px]">
                →
              </span>
            </a>

            <a
              href={fizioWa}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="floating-whatsapp-fizio"
              className="flex items-center gap-3 p-3.5 rounded-2xl bg-white hover:bg-[#F2F9DD] border border-slate-100 hover:border-brand-aqua transition-all group"
            >
              <span className="w-11 h-11 rounded-full bg-brand-aqua text-white flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5" />
              </span>
              <div className="flex-grow min-w-0">
                <p className="font-display text-[14px] font-medium text-slate-900 leading-tight">
                  Fizio & Kinetoterapie
                </p>
                <p className="text-[12px] text-slate-500 mt-0.5 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {CLINIC.fizio.phone}
                </p>
              </div>
              <span className="text-brand-aquaDark opacity-0 group-hover:opacity-100 transition-opacity text-[18px]">
                →
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Închide WhatsApp" : "Deschide WhatsApp"}
        data-testid="floating-whatsapp-toggle"
        className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white flex items-center justify-center shadow-2xl shadow-[#25D366]/40 hover:scale-105 transition-all"
      >
        {!open && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-pulse-ring pointer-events-none" />
        )}
        {open ? (
          <X className="w-6 h-6 relative" />
        ) : (
          <svg viewBox="0 0 32 32" className="w-7 h-7 relative" fill="currentColor" aria-hidden="true">
            <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.04 2.264v.114c-.015.99.34 1.95.945 2.71 1.16 1.5 2.6 2.793 4.302 3.7.622.327 1.276.5 1.96.5 1.013 0 2.12-.62 2.36-1.617.075-.297.116-.617.116-.93 0-.388-1.69-1.276-2.06-1.276zM16.077 6.077C10.617 6.077 6.18 10.517 6.18 16c0 1.747.46 3.485 1.336 4.99l-.86 4.302a.43.43 0 0 0 .547.494l4.13-.84a9.96 9.96 0 0 0 4.745 1.205c5.46 0 9.9-4.44 9.9-9.9s-4.44-9.9-9.9-9.9z" />
          </svg>
        )}
      </button>
    </div>
  );
}
